"use client";

import { useEffect, useState } from "react";

interface ProcessingOverlayProps {
  isProcessing: boolean;
  error: string | null;
  onErrorDismiss?: () => void;
  hasEnhancement?: boolean; // Whether this tool uses post-processing enhancement
  customWaitTime?: string; // Custom average wait time message (e.g., "3-7")
  customMessage?: string; // Custom processing message (e.g., "Removing Background...")
  /** Tight overlay for small surfaces (e.g. the free PFP disc). Skips the mobile scroll-to-top. */
  compact?: boolean;
}

// Mobile browser detection
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export default function ProcessingOverlay({
  isProcessing,
  error,
  onErrorDismiss,
  hasEnhancement = false,
  customWaitTime,
  customMessage,
  compact = false,
}: ProcessingOverlayProps) {
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!isProcessing) {
      setElapsedTime(0);
      return;
    }

    // Scroll to top on mobile when processing starts (full-canvas tools only)
    if (!compact && isMobile()) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000; // Keep as decimal
      setElapsedTime(elapsed);
    }, 100); // Update every 100ms for smooth display

    return () => clearInterval(interval);
  }, [isProcessing, compact]);

  // Don't render if neither processing nor error
  if (!isProcessing && !error) return null;

  // Format elapsed time as seconds with hundredths (e.g., "2.45s")
  const formatTime = (seconds: number) => {
    return `${seconds.toFixed(2)}s`;
  };

  if (compact) {
    return (
      <div className="absolute inset-0 z-20 rounded-full bg-black/70 flex flex-col items-center justify-center px-3 text-center">
        {isProcessing ? (
          <>
            <svg className="animate-spin h-8 w-8 text-white mb-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path className="opacity-75" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M12 2a10 10 0 0110 10" />
            </svg>
            <p className="text-white text-[10px] font-medium leading-tight">
              {customMessage || "Removing background..."}
            </p>
            <p className="text-white/80 text-[10px] mt-0.5" style={{ minHeight: "0.875rem" }}>
              {elapsedTime >= 2 ? formatTime(elapsedTime - 2) : ""}
            </p>
          </>
        ) : error ? (
          <>
            <p className="text-white text-[10px] font-medium leading-tight mb-2">
              {error}
            </p>
            {onErrorDismiss && (
              <button
                type="button"
                onClick={onErrorDismiss}
                className="bg-white text-gray-900 text-[10px] font-semibold px-2.5 py-1 rounded-full"
              >
                Try again
              </button>
            )}
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center z-10">
      <div className="text-center max-w-sm mx-auto p-6">
        {isProcessing ? (
          <>
            {/* Spinner */}
            <svg
              className="animate-spin h-16 w-16 text-white mx-auto mb-6"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                className="opacity-75"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                d="M12 2a10 10 0 0110 10"
              />
            </svg>

            {/* Processing Text with Stage */}
            <p className="text-white text-lg font-medium mb-2">
              {customMessage || (
                hasEnhancement ? (
                  // Headshots: 3 stages (with enhancement)
                  elapsedTime < 2 ? 'Preparing...' :
                  elapsedTime < 19 ? 'Generating...' :
                  'Enhancing...'
                ) : (
                  // Other tools: 2 stages (no enhancement)
                  elapsedTime < 2 ? 'Preparing...' :
                  'Generating...'
                )
              )}
            </p>

            {/* Elapsed Time - hide first 2 seconds of prep time but reserve space */}
            <p className="text-white text-sm font-normal mb-4" style={{ minHeight: '1.25rem' }}>
              {elapsedTime >= 2 ? formatTime(elapsedTime - 2) : '--:--'}
            </p>

            {/* Average Wait Time Pill */}
            <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-4 py-2">
              <svg
                className="w-4 h-4 text-white mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-white text-sm">
                Average wait time: ~{customWaitTime || (hasEnhancement ? '15-45' : '10-20')} seconds
              </span>
            </div>
          </>
        ) : error ? (
          <>
            {/* Error Icon */}
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            {/* Error Title */}
            <p className="text-white text-lg font-medium mb-2">
              Processing Failed
            </p>

            {/* Error Message */}
            <p className="text-white text-sm mb-6 opacity-90">
              {error}
            </p>

            {/* Dismiss Button */}
            {onErrorDismiss && (
              <button
                onClick={onErrorDismiss}
                className="bg-white text-gray-900 px-6 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Try Again
              </button>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
